import { Card, CardContent } from "@/components/ui/card";
import { COPY } from "@/lib/copy";
import { Lock, EyeOff, Server, Trash2, UserCheck, Mail } from "lucide-react";

export default function Privacy() {
  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-3xl">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="privacy-title">Privacy</h1>
        <p className="text-muted-foreground mt-3">How Witness Your Vote Ontario collects, protects, and preserves information.</p>
      </div>
      <div className="mt-10 max-w-3xl space-y-5">
        <Card><CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-2.5 mb-4"><UserCheck className="h-5 w-5 text-primary" /><h2 className="font-heading font-semibold text-lg">What we collect — only with your consent</h2></div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            When you voluntarily witness your vote we collect: your full name, qualifying address (and mailing address if
            different), email, optional phone number, the date and authorized method by which you voted, your declaration
            confirmations, and your electronic signature (typed text or a drawn signature image). Two explicit consents are
            recorded with every declaration: consent to verification contact, and consent to evidence retention.
          </p>
        </CardContent></Card>

        <Card><CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-2.5 mb-4"><Lock className="h-5 w-5 text-destructive" /><h2 className="font-heading font-semibold text-lg">What we never collect</h2></div>
          <ul className="space-y-2">
            {COPY.neverCollect.map((item) => (
              <li key={item} className="flex items-center gap-2.5 text-sm text-foreground"><Lock className="h-3.5 w-3.5 text-destructive shrink-0" /> {item}</li>
            ))}
          </ul>
          <p className="text-sm text-muted-foreground leading-relaxed mt-4">
            Signature uploads are restricted to signature images only, with validation. Any upload suspected of being
            prohibited material — including any ballot image — is quarantined and reviewed; it is never published. There is
            no voters' list import functionality anywhere on this platform.
          </p>
        </CardContent></Card>

        <Card><CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-2.5 mb-4"><EyeOff className="h-5 w-5 text-accent" /><h2 className="font-heading font-semibold text-lg">Who can see your declaration</h2></div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Your declaration is private. It is never shown to the public. Candidates never see declarant names, addresses,
            individual declarations, or live totals — for any candidate, including their own. Only authorized platform
            administrators, bound by role-based access controls, can review declarations for verification and duplicate
            reconciliation. No declaration totals are released by candidate while voting is open.
          </p>
        </CardContent></Card>

        <Card><CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-2.5 mb-4"><Server className="h-5 w-5 text-primary" /><h2 className="font-heading font-semibold text-lg">How records are protected</h2></div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Data is encrypted in transit (HTTPS). Every declaration is sealed with a SHA-256 integrity hash — a cryptographic
            fingerprint that allows you and independent reviewers to confirm the record has not been altered. Administrator
            actions are recorded in an append-only audit log. Administrator accounts use hashed credentials and role-based
            permissions.
          </p>
        </CardContent></Card>

        <Card><CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-2.5 mb-4"><Trash2 className="h-5 w-5 text-slate-500" /><h2 className="font-heading font-semibold text-lg">Retention and corrections</h2></div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Declarations are preserved as evidence and are not silently deleted; corrections are appended to the record with
            an audit trail so the original evidence remains intact. Clearly-labelled TEST data created during platform
            development is removable and is excluded from any live record. Formal retention schedules and evidence-freeze
            procedures are being developed with legal and privacy professionals before any public launch.
          </p>
        </CardContent></Card>

        <Card className="bg-secondary"><CardContent className="p-6">
          <div className="flex items-center gap-2.5 mb-2"><Mail className="h-4 w-4 text-primary" /><h2 className="font-heading font-semibold text-sm">Privacy questions</h2></div>
          <p className="text-sm text-muted-foreground">Contact support@witnessyourvote.ca. This page will be reviewed by a privacy professional before public launch.</p>
        </CardContent></Card>
      </div>
    </div>
  );
}
